import React, { useEffect, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Button, CircularProgress, Grid, Typography, Box, Paper, Chip, Tab, Tabs } from '@mui/material';
import AttendanceForm from './AttendanceForm';
import { API } from 'aws-amplify';
import LeaveForm from './LeaveForm';
import PerformanceMetrics from './PerformanceMetrics';
import LoadingScreen from '../UI/LoadingScreen';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AddIcon from '@mui/icons-material/Add';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import AssessmentIcon from '@mui/icons-material/Assessment';




const localizer = momentLocalizer(moment);
const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '75vh',
        overflow: 'hidden',
        width: "100%",
        backgroundColor: '#f8fafc',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
    },
    notInAttendanceCell: {
        backgroundColor: '#fee2e2 !important',
        borderRadius: '8px !important',
        border: '2px solid #fca5a5 !important',
    },
    futureDateCell: {
        backgroundColor: '#f1f5f9 !important',
        borderRadius: '8px !important',
    },
    currentDateCell: {
        backgroundColor: '#1e40af !important',
        color: "#fff !important",
        borderRadius: '8px !important',
        boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3) !important'
    },
    dayLabel: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        borderRadius: '8px'
    },
    DataCell: {
        backgroundColor: '#dcfce7 !important',
        borderRadius: '8px !important',
        border: '2px solid #86efac !important',
    },
    cardContainer: {
        display: 'flex',
        flexDirection: 'row',
        marginBottom: "24px",
        gap: '16px'
    },
    statsCard: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        borderRadius: '16px !important',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12) !important',
        transition: 'transform 0.2s ease-in-out !important',
        '&:hover': {
            transform: 'translateY(-4px) !important'
        }
    },
    actionButtonContainer: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row'
    }
}));

const Legend = () => {
    const legendItems = [
        { title: 'Present', color: '#dcfce7', borderColor: '#86efac', icon: <EventAvailableIcon sx={{ fontSize: 16 }} /> },
        { title: 'Absent', color: '#fee2e2', borderColor: '#fca5a5', icon: <EventBusyIcon sx={{ fontSize: 16 }} /> },
        { title: 'Current Day', color: "#1e40af", borderColor: "#1e40af", textColor: "#fff", icon: <CalendarTodayIcon sx={{ fontSize: 16 }} /> },
        { title: 'Upcoming Days', color: "#f1f5f9", borderColor: "#cbd5e1", icon: <AccessTimeIcon sx={{ fontSize: 16 }} /> },
    ];

    return (
        <Paper 
            elevation={2} 
            sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                flexWrap: 'wrap',
                gap: 2,
                p: 2, 
                mt: 3,
                borderRadius: '12px',
                backgroundColor: '#ffffff'
            }}
        >
            {legendItems.map((item, index) => (
                <Chip
                    key={index}
                    icon={item.icon}
                    label={item.title}
                    variant="outlined"
                    sx={{
                        backgroundColor: item.color,
                        borderColor: item.borderColor,
                        color: item.textColor || '#374151',
                        fontWeight: 500,
                        '& .MuiChip-icon': {
                            color: item.textColor || '#374151'
                        }
                    }}
                />
            ))}
        </Paper>
    );
};

const AttendanceCalender = () => {
    const classes = useStyles();

    const [leaveFormOpen, setLeaveFormOpen] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [openDialog, setOpenDialog] = useState(false);
    const [attendance, setAttendance] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };
    const handleCalendarSelect = ({ start }) => {
        //console.log(start)
        setSelectedDate(start);
    };

    const getDaysPresented = (month) => {
        const presentedDates = new Set();

        // Get all unique dates in the specified month where user was present
        attendance.forEach((entry) => {
            const entryMonth = moment(entry.date, 'YYYY-MM-DD').format('YYYY-MM');
            if (entryMonth === month && entry.status !== 'false') {
                presentedDates.add(entry.date);
            }
        });

        return presentedDates.size;
    };



    const getDaysOff = (month) => {
        const daysInMonth = moment(month, 'YYYY-MM').daysInMonth();
        const startOfMonth = moment(month, 'YYYY-MM').startOf('month');
        const today = moment();

        let daysOffCount = 0;

        for (let i = 0; i < daysInMonth; i++) {
            const currentDay = startOfMonth.clone().add(i, 'days');
            const currentDayString = currentDay.format('YYYY-MM-DD');
            const isInAttendance = attendance.some((entry) => entry.date === currentDayString);
            const isFutureDate = currentDay.isAfter(today, 'day');
            const isWeekend = currentDay.day() === 0 || currentDay.day() === 6; // Sunday = 0, Saturday = 6

            // Only count weekdays that are not in the future and have no attendance record
            if (!isInAttendance && !isFutureDate && !isWeekend) {
                daysOffCount++;
            }
        }

        return daysOffCount;
    };



    const getTotalAttendance = () => {
        const attendanceCount = {};
        attendance.forEach((entry) => {
            // Create a unique ID for each attendance entry
            const rowId = `${entry.date}-${entry.startTime || entry.time.split(' - ')[0]}-${entry.endTime || entry.time.split(' - ')[1]}`;
            attendanceCount[rowId] = {
                id: rowId,
                date: entry.date,
                time: entry.time,
                agenda: entry.agenda,
                status: entry.status || 'Present'
            };
        });
        return Object.values(attendanceCount);
    };
    const dayPropGetter = (date) => {
        const dateString = moment(date).format('YYYY-MM-DD');
        const isInAttendance = attendance.some((entry) => entry.date === dateString);
        const isFutureDate = moment(date).isAfter(moment(), 'day');
        const isCurrentDate = moment(date).isSame(moment(), 'day');

        if (isInAttendance) {
            return { className: classes.DataCell };
        } else if (isFutureDate) {
            return { className: classes.futureDateCell };
        } else if (isCurrentDate) {
            return { className: classes.currentDateCell };
        } else {
            return { className: classes.notInAttendanceCell };
        }
    };
    const rows = getTotalAttendance();
    const eventStyleGetter = (event, start, end, isSelected) => {
        //console.log(event)
        const style = {
            backgroundColor: event.title.includes('Present') ? '#000' : 'red',
            color: 'white',
        };

        return {
            style,
        };
    };
    const DayCell = ({ date, label }) => {
        //console.log(date, label)
        return (
            <div className={classes.dayLabel}>
                <div>{moment(date).format('DD')}</div>
                <div>{"label"}</div>
            </div>
        );
    };
    const getTotalHoursSpent = (month) => {
        // Filter attendance for the specific month
        const attendanceForMonth = attendance.filter((entry) => {
            const entryMonth = moment(entry.date, 'YYYY-MM-DD').format('YYYY-MM');
            return entryMonth === month;
        });

        // Group attendance by date to handle daily time ranges properly
        const dailyAttendance = {};
        attendanceForMonth.forEach((entry) => {
            if (!dailyAttendance[entry.date]) {
                dailyAttendance[entry.date] = [];
            }
            dailyAttendance[entry.date].push(entry);
        });

        let totalMonthHours = 0;

        // Calculate hours for each day and sum them up
        Object.keys(dailyAttendance).forEach((date) => {
            const dayEntries = dailyAttendance[date];
            const timeRanges = [];

            dayEntries.forEach((entry) => {
                try {
                    let startTime, endTime;
                    
                    // Use individual startTime and endTime fields if available
                    if (entry.startTime && entry.endTime) {
                        startTime = moment(`${entry.date} ${entry.startTime}`, 'YYYY-MM-DD HH:mm');
                        endTime = moment(`${entry.date} ${entry.endTime}`, 'YYYY-MM-DD HH:mm');
                    } else if (entry.time && entry.time.includes(' - ')) {
                        const timeRange = entry.time.split(' - ');
                        if (timeRange.length === 2) {
                            const startTimeStr = timeRange[0].trim();
                            const endTimeStr = timeRange[1].trim();
                            
                            if (startTimeStr.match(/^\d{1,2}:\d{2}$/) && endTimeStr.match(/^\d{1,2}:\d{2}$/)) {
                                startTime = moment(`${entry.date} ${startTimeStr}`, 'YYYY-MM-DD HH:mm');
                                endTime = moment(`${entry.date} ${endTimeStr}`, 'YYYY-MM-DD HH:mm');
                            } else {
                                startTime = moment(`${entry.date} ${startTimeStr}`, 'YYYY-MM-DD h:mm A');
                                endTime = moment(`${entry.date} ${endTimeStr}`, 'YYYY-MM-DD h:mm A');
                            }
                        }
                    }

                    if (startTime && endTime && startTime.isValid() && endTime.isValid()) {
                        if (endTime.isBefore(startTime)) {
                            endTime.add(1, 'day');
                        }
                        
                        const hoursSpent = endTime.diff(startTime, 'hours', true);
                        if (hoursSpent > 0 && hoursSpent <= 24) {
                            timeRanges.push({
                                start: startTime.clone(),
                                end: endTime.clone()
                            });
                        }
                    }
                } catch (error) {
                    console.warn('Error parsing time for entry:', entry, error);
                }
            });

            // Merge overlapping time ranges for this day
            timeRanges.sort((a, b) => a.start - b.start);
            let mergedRanges = [];

            timeRanges.forEach((range) => {
                if (mergedRanges.length === 0) {
                    mergedRanges.push(range);
                } else {
                    const lastRange = mergedRanges[mergedRanges.length - 1];
                    if (range.start.isSameOrBefore(lastRange.end)) {
                        if (range.end.isAfter(lastRange.end)) {
                            lastRange.end = range.end.clone();
                        }
                    } else {
                        mergedRanges.push(range);
                    }
                }
            });

            // Add this day's hours to the monthly total
            mergedRanges.forEach((range) => {
                totalMonthHours += range.end.diff(range.start, 'hours', true);
            });
        });

        return totalMonthHours.toFixed(2);
    };

    const getTodayHoursSpent = () => {
        const today = moment().format('YYYY-MM-DD');
        const todayAttendance = attendance.filter((entry) => entry.date === today);

        // Collect all valid time ranges for today
        const timeRanges = [];

        todayAttendance.forEach((entry) => {
            try {
                let startTime, endTime;
                
                // Use individual startTime and endTime fields if available
                if (entry.startTime && entry.endTime) {
                    startTime = moment(`${entry.date} ${entry.startTime}`, 'YYYY-MM-DD HH:mm');
                    endTime = moment(`${entry.date} ${entry.endTime}`, 'YYYY-MM-DD HH:mm');
                } else if (entry.time && entry.time.includes(' - ')) {
                    const timeRange = entry.time.split(' - ');
                    if (timeRange.length === 2) {
                        const startTimeStr = timeRange[0].trim();
                        const endTimeStr = timeRange[1].trim();
                        
                        if (startTimeStr.match(/^\d{1,2}:\d{2}$/) && endTimeStr.match(/^\d{1,2}:\d{2}$/)) {
                            startTime = moment(`${entry.date} ${startTimeStr}`, 'YYYY-MM-DD HH:mm');
                            endTime = moment(`${entry.date} ${endTimeStr}`, 'YYYY-MM-DD HH:mm');
                        } else {
                            startTime = moment(`${entry.date} ${startTimeStr}`, 'YYYY-MM-DD h:mm A');
                            endTime = moment(`${entry.date} ${endTimeStr}`, 'YYYY-MM-DD h:mm A');
                        }
                    }
                }

                if (startTime && endTime && startTime.isValid() && endTime.isValid()) {
                    if (endTime.isBefore(startTime)) {
                        endTime.add(1, 'day');
                    }
                    
                    const hoursSpent = endTime.diff(startTime, 'hours', true);
                    if (hoursSpent > 0 && hoursSpent <= 24) {
                        timeRanges.push({
                            start: startTime.clone(),
                            end: endTime.clone(),
                            hours: hoursSpent
                        });
                    }
                }
            } catch (error) {
                console.warn('Error parsing time for today entry:', entry, error);
            }
        });

        // Sort time ranges by start time
        timeRanges.sort((a, b) => a.start - b.start);

        // Merge overlapping time ranges and calculate total hours
        let totalHours = 0;
        let mergedRanges = [];

        timeRanges.forEach((range) => {
            if (mergedRanges.length === 0) {
                mergedRanges.push(range);
            } else {
                const lastRange = mergedRanges[mergedRanges.length - 1];
                
                // Check if current range overlaps with the last merged range
                if (range.start.isSameOrBefore(lastRange.end)) {
                    // Merge ranges - extend the end time if current range ends later
                    if (range.end.isAfter(lastRange.end)) {
                        lastRange.end = range.end.clone();
                    }
                } else {
                    // No overlap, add as new range
                    mergedRanges.push(range);
                }
            }
        });

        // Calculate total hours from merged ranges
        mergedRanges.forEach((range) => {
            totalHours += range.end.diff(range.start, 'hours', true);
        });

        return totalHours.toFixed(2);
    };
    
    const handleAddAttendance = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        getAttendance();
    };

    const handleLeave = () => {
        setLeaveFormOpen(true); // Open the LeaveForm modal
    };

    const handleCloseLeaveForm = () => {
        setLeaveFormOpen(false); // Close the LeaveForm modal
    };

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const MonthCard = ({ month }) => {
        const classes = useStyles();
        const daysPresented = getDaysPresented(month);
        const daysAbsented = getDaysOff(month);
        const totalHoursSpent = getTotalHoursSpent(month);
        const theme = useTheme();
        const matches = useMediaQuery(theme.breakpoints.down('sm'));

        const todayHours = getTodayHoursSpent();
        
        const statsCards = [
            {
                title: 'Days Present',
                value: daysPresented,
                icon: <EventAvailableIcon sx={{ fontSize: 24, color: '#10b981' }} />,
                gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                bgColor: '#ecfdf5'
            },
            {
                title: 'Days Off',
                value: daysAbsented,
                icon: <EventBusyIcon sx={{ fontSize: 24, color: '#ef4444' }} />,
                gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                bgColor: '#fef2f2'
            },
            {
                title: 'Month Hours',
                value: `${totalHoursSpent}h`,
                icon: <AccessTimeIcon sx={{ fontSize: 24, color: '#3b82f6' }} />,
                gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                bgColor: '#eff6ff'
            },
            {
                title: 'Today Hours',
                value: `${todayHours}h`,
                icon: <CalendarTodayIcon sx={{ fontSize: 24, color: '#8b5cf6' }} />,
                gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                bgColor: '#f3e8ff'
            }
        ];

        return (
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {statsCards.map((card, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Paper
                            elevation={4}
                            sx={{
                                p: 3,
                                borderRadius: '16px',
                                background: card.bgColor,
                                border: '1px solid rgba(0,0,0,0.06)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 700, 
                                        fontSize: { xs: '28px', sm: '32px', md: '36px' },
                                        background: card.gradient,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        mb: 1
                                    }}>
                                        {card.value}
                                    </Typography>
                                    <Typography variant="body1" sx={{ 
                                        color: '#64748b', 
                                        fontWeight: 500,
                                        fontSize: '14px'
                                    }}>
                                        {card.title}
                                    </Typography>
                                </Box>
                                <Box sx={{ 
                                    p: 2, 
                                    borderRadius: '12px', 
                                    backgroundColor: 'rgba(255,255,255,0.7)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {card.icon}
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                ))}

                <Grid item xs={12} sm={12}>
                    <Box className={classes.actionButtonContainer} sx={{ justifyContent: 'center', mt: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddAttendance}
                            sx={{
                                textTransform: 'capitalize',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                fontSize: matches ? '11px' : '13px',
                                borderRadius: '8px',
                                px: 2.5,
                                py: 1,
                                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #5a6fd8 0%, #684a94 100%)',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Add Note
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<EventBusyIcon />}
                            onClick={handleLeave}
                            sx={{
                                textTransform: 'capitalize',
                                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                fontSize: matches ? '11px' : '13px',
                                borderRadius: '8px',
                                px: 2.5,
                                py: 1,
                                boxShadow: '0 2px 8px rgba(245, 87, 108, 0.3)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #ed7de4 0%, #f04561 100%)',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(245, 87, 108, 0.4)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Leave Request
                        </Button>
                    </Box>
                </Grid>

                <AttendanceForm open={openDialog} onClose={handleCloseDialog} />
                <LeaveForm open={leaveFormOpen} onClose={handleCloseLeaveForm} />
            </Grid>
        );
    };


    const getAttendance = () => {
        let apiName = 'Node-API';
        let path = ''; //replace this with the path you have configured on your API
        let params = {
            body:
            {
                "command": "getAttendance"
            },
            response: true,
            convertEmptyValues: true,
            headers: {}
        }
        API.post(apiName, path, params).then(response => {
            if (response.data.statusCode === 200) {
                let result = JSON.parse(response.data.body);
                const currentUser = localStorage.getItem("user");
                let resultData = [];
                result.attendance.map((item, index) => {
                    if (item.email.S === currentUser) {
                        let data = {
                            id: index + 1,
                            time: item.startTime.S + " - " + item.endTime.S,
                            startTime: item.startTime.S,
                            endTime: item.endTime.S,
                            email: item.email.S,
                            agenda: item.agenda.S,
                            date: item.date.S,
                            status: item.status.S
                        };
                        resultData.push(data);
                    }
                })
                setAttendance(resultData);
                setIsLoading(false);
            }
        }
        ).catch(error => {
            console.log(error, 'Unable to load the data. Please try again.');
            setIsLoading(false);
        });
    };

    useEffect(() => {
        getAttendance();
    }, [])
    return (
        <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            <Paper 
                elevation={6} 
                sx={{ 
                    p: 4, 
                    borderRadius: '20px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
                }}
            >
                {!isLoading ? (
                    <>
                        <Box sx={{ mb: 4, textAlign: 'left' }}>
                            <Typography variant="h4" sx={{ 
                                fontWeight: 700, 
                                color: '#1e293b',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                mb: 1
                            }}>
                                Minute Minder
                            </Typography>
                            <Typography variant="body1" sx={{ 
                                color: '#64748b', 
                                fontWeight: 400,
                                lineHeight: 1.5
                            }}>
                                Track your daily work hours with precision and ease.
                            </Typography>
                        </Box>

                        {/* Tabs Navigation */}
                        <Paper sx={{ mb: 3, borderRadius: '12px', overflow: 'hidden' }}>
                            <Tabs 
                                value={currentTab} 
                                onChange={handleTabChange}
                                sx={{
                                    '& .MuiTab-root': {
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        minHeight: 56
                                    },
                                    '& .MuiTabs-indicator': {
                                        height: 3,
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    }
                                }}
                            >
                                <Tab 
                                    icon={<CalendarTodayIcon />} 
                                    label="Dashboard" 
                                    iconPosition="start"
                                />
                                <Tab 
                                    icon={<AssessmentIcon />} 
                                    label="Performance Metrics" 
                                    iconPosition="start"
                                />
                            </Tabs>
                        </Paper>
                        
                        {/* Tab Content */}
                        {currentTab === 0 && (
                            <>
                                <MonthCard month={moment().format('YYYY-MM')} />
                                
                                <Box className={classes.container} sx={{ mt: 4 }}>
                            <Calendar
                                localizer={localizer}
                                onSelectSlot={handleCalendarSelect}
                                events={rows.map((row) => ({
                                    title: `${row.agenda}`,
                                    start: moment(`${row.date} ${row.time.split(' - ')[0]}`, 'YYYY-MM-DD h:mm A').toDate(),
                                    end: moment(`${row.date} ${row.time.split(' - ')[1]}`, 'YYYY-MM-DD h:mm A').toDate(),
                                }))}
                                components={{
                                    dayCell: DayCell,
                                }}
                                startAccessor="start"
                                endAccessor="end"
                                onSelectEvent={(event) => handleDateChange(event.start)}
                                eventStyleGetter={eventStyleGetter}
                                dayPropGetter={dayPropGetter}
                                views={['month', 'week', 'day']}
                                style={{
                                    flex: 1,
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '16px',
                                    backgroundColor: '#ffffff',
                                    padding: '24px',
                                    fontFamily: '"Inter", "Segoe UI", sans-serif'
                                }}
                                eventPropGetter={(event) => {
                                    let newStyle = {
                                        color: '#fff',
                                        borderRadius: '8px',
                                        border: 'none',
                                        textAlign: 'center',
                                        fontWeight: 500,
                                        fontSize: '12px',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                                    };
                                    return { style: newStyle };
                                }}
                                formats={{
                                    monthHeaderFormat: (date, culture, localizer) => {
                                        const monthText = localizer.format(date, 'MMMM', culture);
                                        const yearText = localizer.format(date, 'YYYY', culture);
                                        return `${monthText} ${yearText}`;
                                    },
                                    eventTimeRangeFormat: () => null,
                                }}
                                onNavigate={(event) => {
                                    setSelectedDate(event);
                                }}
                                dayClassName={(date, view) => {
                                    return view === 'month' ? 'month-view-day' : 'other-view-day';
                                }}
                            />
                            <Legend />
                        </Box>
                            </>
                        )}

                        {currentTab === 1 && (
                            <PerformanceMetrics attendance={attendance} />
                        )}
                    </>
                ) : (
                    <LoadingScreen message="Loading attendance data..." />
                )}
            </Paper>
        </Box>
    );
};



export default AttendanceCalender;
